import {
  ArrowDown, Calendar, ChevronDown, ChevronUp, Download, Search, User, UserCheck, UserX, Users, XCircle,
} from 'react-feather';
import { ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';

import { GetParticipantDataQuery as GetParticipantData, Permission, RegistrationFilters } from '__generated__/graphql';
import DateRange from '../../../../common/Common/DateRange';
import GetRegistrationSummaryQuery from './GetRegistrationSummaryQuery';
import ProtectedSection from '../../../Common/ProtectedSection';
import RegistrationDetails from './RegistrationDetails';
import SellableLabel from '../../../../common/Common/SellableLabel';
import UI from '../../../../common/UI';
import config from '../../../config';
import useLocale from '../../../../common/useLocale';
import useQuery from '../../../../api/useQuery';
import useUrlState from '../../../../common/useUrlState';

type RegistrationSummaryType = GetParticipantData['project']['participant']['registration_summaries'][0];

export interface RegistrationSummaryProps {
  registrationSummary: RegistrationSummaryType;
  participantId: string;
  to: string;
  open: boolean;
  muted?: boolean;
  perPage?: number;
}

const RegistrationSummary = ({
  registrationSummary, participantId, open, to, muted, perPage,
}: RegistrationSummaryProps) => (
  <UI.CollapsibleNavCard
    to={to}
    open={open}
    muted={false}
    preview={(
      <RegistrationSummaryPreview
        registrationSummary={registrationSummary}
        participantId={participantId}
        open={open}
      />
    )}
    body={(
      <RegistrationSummaryBody
        participantId={participantId}
        eventId={registrationSummary.event.id}
        perPage={perPage}
      />
    )}
    id={`RegistrationSummary_${registrationSummary.event.id}`}
    sc={{ hasPanels: true, muted }}
    ariaLabel={registrationSummary.event.title}
  />
);

interface RegistrationSummaryPreviewProps {
  registrationSummary: RegistrationSummaryType;
  participantId: string;
  open: boolean;
}

const RegistrationSummaryPreview = ({ registrationSummary, participantId, open }: RegistrationSummaryPreviewProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();

  return (
    <UI.GridContainer sc={{
      alignVertical: 'center',
      columns: ['1fr 1fr', '1fr fit-content(120px) fit-content(100px)'],
      width: '100%',
    }}
    >
      <UI.FlexContainer
        sc={{
          alignItems: 'center',
          muted: registrationSummary.active_registrations_count === 0,
          column: ['1 / span 2', '1'],
        }}
      >
        <UI.ProfilePic sc={{
          background: registrationSummary.event.logo?.url ? 'white' : undefined,
          size: 40,
          color: 'white',
          mr: 2,
        }}
        >
          {registrationSummary.event.logo?.url ? (
            <img src={registrationSummary.event.logo.url} alt={registrationSummary.event.title} />
          ) : (
            <UI.Span
              sc={{ textAlign: 'center' }}
              style={{ fontSize: '0.6rem' }}
            >
              {registrationSummary.event.title.charAt(0)}
            </UI.Span>
          )}
        </UI.ProfilePic>

        <UI.Div>
          <UI.H3 sc={{ strong: open }}>
            {registrationSummary.event.title}
          </UI.H3>
          <UI.Span sc={{ color: 'gray.500' }}>
            <UI.Icon sc={{ mr: 1 }}>
              <Calendar />
            </UI.Icon>

            <DateRange start={registrationSummary.event.start_date} end={registrationSummary.event.end_date} />
          </UI.Span>
        </UI.Div>
      </UI.FlexContainer>

      <UI.Div>
        <SellableLabel sc={{ muted: registrationSummary.active_registrations_count === 0 }}>
          {t('n_tickets', {
            count: registrationSummary.active_registrations_count,
            value: formatNumber(registrationSummary.active_registrations_count),
          })}
        </SellableLabel>
      </UI.Div>

      <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'end' }}>
        <ProtectedSection permissions={[Permission.exports]}>
          {registrationSummary.active_registrations_count > 0 && (
            <UI.TooltipContainer sc={{ mr: 1 }}>
              <UI.Tooltip sc={{ alignH: 'center' }}>
                {t('export_registrations')}
              </UI.Tooltip>
              <UI.AButton
                href={`${config.backendPath}/api/exports/registrations/${participantId}/${registrationSummary.event.id}`}
                sc={{ blank: true, size: 'sm', color: 'gray.600' }}
                aria-label={t('export_registrations', {
                  count: registrationSummary.active_registrations_count,
                })}
              >
                <UI.Icon>
                  <Download />
                </UI.Icon>
              </UI.AButton>
            </UI.TooltipContainer>
          )}
        </ProtectedSection>
        <UI.Icon sc={{ color: 'gray.600', px: 1, mb: 0 }}>
          {open ? <ChevronUp /> : <ChevronDown />}
        </UI.Icon>
      </UI.FlexContainer>
    </UI.GridContainer>
  );
};

interface RegistrationSummaryBodyProps {
  participantId: string;
  eventId: string;
  perPage?: number;
}

const initialFilters: RegistrationFilters = {
  assigned: null,
  tickets: [],
  teams: [],
  has_team: null,
};

const RegistrationSummaryBody = ({ participantId, eventId, perPage = 10 }: RegistrationSummaryBodyProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();

  const [urlState, setUrlState] = useUrlState({
    parse: (rawParams, { string }) => ({
      ...rawParams,
      search: string(rawParams.search),
      event: string(rawParams.event),
    }),
    onChange: (urlState) => {
      if (!isEqual(filters, urlState)) {
        if (searchRef.current) {
          searchRef.current.value = urlState.search || '';
        }
      }
    },
  });

  const [limit, setLimit] = useState(perPage);

  const [filters, setFilters] = useState<RegistrationFilters>(initialFilters);

  const hasSearchFilter = !!urlState.search;
  const hasAssignmentFilter = filters.assigned !== initialFilters.assigned;
  const hasTicketFilter = filters.tickets !== initialFilters.tickets;
  const hasTeamFilter = filters.teams !== initialFilters.teams || filters.has_team !== initialFilters.has_team;

  const hasFilters = hasSearchFilter
    || hasAssignmentFilter
    || hasTicketFilter
    || hasTeamFilter;

  const searchRef = useRef<HTMLInputElement>();

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value;

    setUrlState((filters) => ({ ...filters, search }), true);
  };

  const handleAssignedChange = (assigned: boolean | null) => {
    setFilters((filters) => ({
      ...filters,
      assigned,
    }));
  };

  const handleTicketChange = (event:ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;

    setFilters((filters) => ({
      ...filters,
      tickets: value === 'all' ? initialFilters.tickets : [value],
    }));
  };

  const handleTeamChange = (event:ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;

    setFilters((filters) => ({
      ...filters,
      teams: value === 'all' || value === 'no_team' ? initialFilters.teams : [value],
      has_team: value !== 'no_team' ? initialFilters.has_team : false,
    }));
  };

  /**
   * Unsets the search term in the params and empties the input.
   */
  const resetSearch = () => {
    setUrlState((filters) => ({ ...filters, search: undefined }), true);
    searchRef.current.value = '';
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setUrlState((params) => ({ ...params, search: undefined }), true);
    searchRef.current.value = '';
  };

  const { data, loading } = useQuery(
    GetRegistrationSummaryQuery,
    {
      variables: {
        event_id: eventId,
        participant_id: participantId,
        filters: {
          ...filters,
          search: urlState.search,
        },
        limit,
      },
    },
  );

  if (!data) {
    return <UI.PageLoader height={185} />; // Min height of a single registration card
  }

  const { registrationSummary } = data;
  const { registrations } = registrationSummary;
  const count = registrationSummary.filtered_registrations_count;

  const sequenceMatches = urlState.search?.match(/ATL(.*)/i)?.[1];
  const isSequenceMatch = registrations.length === 1 && sequenceMatches === registrations[0].sequence.toString();

  const showFilters = registrationSummary.registrations_count > 1;

  const showMore = () => setLimit((limit) => limit + perPage);
  const showLess = () => setLimit(perPage);

  return (
    <UI.FadeIn>
      <UI.GridContainer>
        {showFilters && (
          <>
            <UI.FormGrid sc={{ gutter: 0.75 }}>
              <UI.FormGrid sc={{ columns: '1fr 1fr 1fr', gutter: 0.5 }}>
                <UI.InputGroup>
                  <UI.InputWrapper>
                    <UI.Span sc={{ muted: !hasSearchFilter }}>
                      <UI.Icon>
                        <Search />
                      </UI.Icon>
                    </UI.Span>
                    <UI.DebouncedInput
                      type="text"
                      value={urlState.search}
                      onChange={handleSearch}
                      placeholder={t('search')}
                      ref={searchRef}
                      sc={{ px: 5 }}
                      style={{ fontWeight: isSequenceMatch ? '500' : undefined }}
                    />
                    {urlState.search && (
                      <UI.Span>
                        <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400', flex: { alignItems: 'center' } }}>
                          <UI.Icon>
                            <XCircle />
                          </UI.Icon>
                        </UI.A>
                      </UI.Span>
                    )}
                  </UI.InputWrapper>
                </UI.InputGroup>
                <UI.InputGroup>
                  <UI.InputWrapper>
                    <UI.Span sc={{ muted: !hasTicketFilter }}>
                      <UI.Icon>
                        <UI.Icons.Ticket />
                      </UI.Icon>
                    </UI.Span>
                    <UI.Select
                      value={!hasTicketFilter ? 'all' : filters.tickets[0] || ''}
                      onChange={handleTicketChange}
                      sc={{
                        pl: 5,
                        hasValue: hasTicketFilter,
                      }}
                    >
                      <option value="all">
                        {hasTicketFilter ? t('all_tickets') : t('filter_by_ticket')}
                      </option>
                      {registrationSummary.tickets.map((ticket) => (
                        <option value={ticket.id} key={ticket.id}>
                          {ticket.title}
                        </option>
                      ))}
                    </UI.Select>
                  </UI.InputWrapper>
                </UI.InputGroup>
                <UI.InputGroup>
                  <UI.InputWrapper>
                    <UI.Span sc={{ muted: !hasTeamFilter }}>
                      <UI.Icon>
                        <Users />
                      </UI.Icon>
                    </UI.Span>
                    <UI.Select
                      value={!hasTeamFilter
                        ? 'all'
                        : (filters.has_team === false ? 'no_team' : filters.teams[0] || '')}
                      onChange={handleTeamChange}
                      sc={{
                        pl: 5,
                        hasValue: hasTeamFilter,
                      }}
                    >
                      <option value="all">
                        {hasTeamFilter ? t('all_teams') : t('filter_by_team')}
                      </option>
                      <option value="no_team">
                        {t('no_team')}
                      </option>
                      {registrationSummary.teams.map((team) => (
                        <option value={team.id} key={team.id}>
                          {team.title}
                        </option>
                      ))}
                    </UI.Select>
                  </UI.InputWrapper>
                </UI.InputGroup>
              </UI.FormGrid>

              <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <UI.InputGroup>
                  <UI.Radio
                    checked={!hasAssignmentFilter}
                    onChange={() => handleAssignedChange(null)}
                    sc={{ fontWeight: 500, inlineBlock: true, color: hasAssignmentFilter && 'gray.500', mr: 3 }}
                  >
                    <UI.Icon>
                      <User />
                    </UI.Icon>
                    {' '}
                    {t('all')}
                    <UI.Span sc={{ muted: true, fontWeight: 400 }}>
                      {' ('}
                      {formatNumber(registrationSummary.registrations_count)}
                      )
                    </UI.Span>
                  </UI.Radio>
                  <UI.Radio
                    checked={filters.assigned === true}
                    onChange={() => handleAssignedChange(true)}
                    sc={{ fontWeight: 500, inlineBlock: true, color: filters.assigned !== true && 'gray.500', mr: 3 }}
                  >
                    <UI.Icon>
                      <UserCheck />
                    </UI.Icon>
                    {' '}
                    {t('assigned')}
                    <UI.Span sc={{ muted: true, fontWeight: 400 }}>
                      {' ('}
                      {formatNumber(registrationSummary.assigned_registrations_count)}
                      )
                    </UI.Span>
                  </UI.Radio>
                  <UI.Radio
                    checked={filters.assigned === false}
                    onChange={() => handleAssignedChange(false)}
                    sc={{ fontWeight: 500, inlineBlock: true, color: filters.assigned !== false && 'gray.500' }}
                  >
                    <UI.Icon>
                      <UserX />
                    </UI.Icon>
                    {' '}
                    {t('unassigned')}
                    <UI.Span sc={{ muted: true, fontWeight: 400 }}>
                      {' ('}
                      {formatNumber(registrationSummary.unassigned_registrations_count)}
                      )
                    </UI.Span>
                  </UI.Radio>
                </UI.InputGroup>
                <UI.FlexContainer sc={{ alignItems: 'center' }}>
                  {hasFilters && (
                    <UI.FadeIn sc={{ delay: false, fontWeight: 500 }}>
                      <UI.A
                        onClick={() => resetFilters()}
                        role="button"
                        sc={{ color: loading ? 'gray.400' : undefined, small: true }}
                      >
                        <UI.Icon>
                          <XCircle />
                        </UI.Icon>
                        {' '}
                        {t('clear_filters')}
                      </UI.A>
                    </UI.FadeIn>
                  )}
                </UI.FlexContainer>
              </UI.FlexContainer>
            </UI.FormGrid>
          </>
        )}

        <UI.GridContainer sc={{ gutter: 0.5, loading }}>
          {registrations.map((registration) => (
            <RegistrationDetails
              registration={registration}
              eventId={eventId}
              participantId={participantId}
              highlight={isSequenceMatch}
              expanded={isSequenceMatch || registrationSummary.registrations_count === 1}
              key={registration.id}
            />
          ))}

          {registrations.length === 0 && (
            <UI.Card sc={{ background: 'gray.50', outline: 'gray.200', color: 'gray.500', textAlign: 'center' }}>
              {t('no_results')}
              {!showFilters && hasFilters && (
                <UI.A
                  onClick={() => resetFilters()}
                  role="button"
                  sc={{ color: loading ? 'gray.400' : undefined, ml: 4 }}
                >
                  <UI.Icon>
                    <XCircle />
                  </UI.Icon>
                  {' '}
                  {t('clear_filters')}
                </UI.A>
              )}
            </UI.Card>
          )}
        </UI.GridContainer>

        {count > perPage && (
          <UI.Div>
            <UI.Button
              onClick={() => showMore()}
              sc={{
                outline: true,
                brand: 'gray.200',
                color: 'gray.800',
                borderRadius: 'xl',
                loading,
              }}
              disabled={loading || limit >= count}
            >
              <UI.Icon>
                <ArrowDown />
              </UI.Icon>
              {' '}
              {t('show_more')}
              {' '}
              <UI.Span sc={{ muted: true }}>
                (
                {t('n_remaining', { value: formatNumber(Math.max(count - limit, 0)) })}
                )
              </UI.Span>
            </UI.Button>
            {' '}
            {limit > perPage && (
              <UI.Button
                onClick={() => showLess()}
                sc={{
                  outline: true,
                  brand: 'gray.200',
                  color: 'gray.800',
                  borderRadius: 'xl',
                  loading,
                }}
                disabled={loading}
              >
                {t('show_less')}
              </UI.Button>
            )}
          </UI.Div>
        )}
      </UI.GridContainer>
    </UI.FadeIn>
  );
};

export default RegistrationSummary;
